import { Injectable } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { TranslatorService } from 'src/core/translator/domain/translator-service';

@Injectable({
  providedIn: 'root',
})
export class MessageHelper {
  constructor(
    private readonly alertController: AlertController,
    private readonly toastCtrl: ToastController,
    private readonly translation: TranslatorService
  ) {}

  async showSuccess(message: string, duration: number = 1000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      color: 'success',
      duration,
      cssClass: 'hack-toast-tap-to-dismiss',
    });
    await toast.present();
  }

  async showWarning(message: string, duration: number = 2000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      color: 'warning',
      duration,
      cssClass: 'hack-toast-tap-to-dismiss',
    });
    await toast.present();
  }

  async showInfo(message: string, duration: number = 2000, showCloseButton: boolean = false): Promise<void> {
    const buttons = [];
    const ok = this.translation.translate('COMMON.OK');
    buttons.push({
      text: ok,
      role: 'cancel',
      handler: () => {},
    });
    if (showCloseButton) {
      // In this case we should not use our hack tap to dismiss since there already is a button
      const toast = await this.toastCtrl.create({
        message,
        duration,
        color: 'medium',
        buttons,
      });
      await toast.present();
    } else {
      // If no close button, apply the toast tap to dismiss hack
      const toast = await this.toastCtrl.create({
        message,
        duration,
        color: 'medium',
        buttons,
        cssClass: 'hack-toast-tap-to-dismiss',
      });
      await toast.present();
    }
  }

  async showInfoWithAction(
    message: string,
    actionText: string,
    handler: () => void | Promise<void>,
    duration: number = 5000
  ): Promise<void> {
    const toast = await this.toastCtrl.create({
      position: 'top',
      message,
      duration,
      color: 'medium',
      buttons: [
        {
          text: actionText,
          handler: () => {
            handler();
          },
        },
      ],
    });

    // add click listener to toast so tapping any part of the toast other than button just dismisses it early.
    toast.addEventListener('click', (event: Event) => {
      // composedPath() crosses the shadow DOM, so the toast button is included when it was tapped.
      const tappedActionButton = event
        .composedPath()
        .some((element) => element instanceof HTMLElement && element.tagName === 'BUTTON');
      if (!tappedActionButton) {
        toast.dismiss();
      }
    });

    await toast.present();
  }

  async showError(message: string): Promise<void> {
    const ok = this.translation.translate('COMMON.OK');
    const toast = await this.toastCtrl.create({
      message,
      color: 'danger',
      buttons: [
        {
          text: ok,
          role: 'cancel',
          handler: () => {},
        },
      ],
    });
    await toast.present();
  }

  async showAlertError(header: string, message: string): Promise<void> {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: ['OK'],
    });
    await alert.present();
  }

  async askConfirmation(header: string, message: string, okButtonText: string = null): Promise<boolean> {
    const ok = okButtonText ?? this.translation.translate('COMMON.OK');
    const cancel = this.translation.translate('COMMON.CANCEL');

    const alert = await this.alertController.create({
      header,
      message,
      buttons: [
        { text: cancel, role: 'cancel' },
        { text: ok, role: 'confirm' },
      ],
    });
    await alert.present();

    // Cerrar por el backdrop o con el botón atrás llega aquí con otro role, que es
    // justo lo que queremos leer como "no confirmado".
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  async showNeedsConnectionInfo() {
    await this.showWarning(this.translation.translate('MESSAGES.NEEDS_CONNECTION'));
  }

  async askConfirmationAboutLosingPendingChanges(): Promise<boolean> {
    const okButtonText = this.translation.translate('COMMON.CONTINUE_BTN');
    const header = this.translation.translate('LOGOUT.PENDING_CHANGES_TITLE');
    const message = this.translation.translate('LOGOUT.PENDING_CHANGES_MESSAGE');

    return await this.askConfirmation(header, message, okButtonText);
  }
}
